import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { LogOut, Menu as MenuIcon, Settings, X } from 'lucide-react'
import {
  Link,
  useMatchRoute,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router'
import { Avatar, Badge, Button, Collapse, Menu, Text } from '@mantine/core'
import { useQueries } from '@tanstack/react-query'
import OneSignal from 'react-onesignal'
import { useMediaQuery } from '@mantine/hooks'
import KohbusLogo from '@/assets/media/svgs/kohbus-logo.svg'
import {
  getTabListQueryOptions,
  getUserProfileQueryOptions,
} from '@/queries/query'
import { isHttpError } from '@/utils/http'
import { getNotificationTabListQueryOptions } from '@/queries/query/notification'
import { env } from '@/env'

const Header = () => {
  const initializeOnesignal = useRef<boolean>(false)
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const isSignoutPage = matchRoute({
    to: '/auth/logout',
  })
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isAuthenticated = useRouteContext({
    strict: false,
    select: (s) => s.authContext?.isAuthenticated || false,
  })

  const [getProfileQuery, getMyTabQuery, getNotificationTabListQuery] =
    useQueries({
      queries: [
        getUserProfileQueryOptions({
          enabled: isAuthenticated,
        }),
        getTabListQueryOptions({
          enabled: isAuthenticated,
        }),
        getNotificationTabListQueryOptions({
          enabled: isAuthenticated,
        }),
      ],
    })

  const { data: userProfile, error, isError } = getProfileQuery
  const { data: resMyTabList } = getMyTabQuery
  const { data: notificationTabList, isSuccess: isNotificationTabListSuccess } =
    getNotificationTabListQuery

  const isMobileViewport = useMediaQuery('(max-width: 48rem)')

  const myBookingCount = useMemo(() => {
    if (resMyTabList?.data) {
      return (
        resMyTabList.data.draft +
        resMyTabList.data.waiting_payment +
        resMyTabList.data.confirmed
      )
    }
    return 0
  }, [resMyTabList?.data])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  useEffect(() => {
    if (isError && isHttpError(error, 401) && !isSignoutPage) {
      navigate({
        to: '/auth/logout',
      })
    }
  }, [isSignoutPage, isError, error])

  useEffect(() => {
    const onesignalAppId = env.VITE_ONESIGNAL_APP_ID
    if (
      !onesignalAppId ||
      !userProfile ||
      initializeOnesignal.current ||
      OneSignal.User.onesignalId
    )
      return

    const initOneSignal = async () => {
      OneSignal.init({
        appId: onesignalAppId,
        safari_web_id: env.VITE_ONESIGNAL_SAFARI_ID,
        allowLocalhostAsSecureOrigin: true,
        autoResubscribe: true,
        welcomeNotification: {
          title: 'KohBus',
          message: 'Thanks for subscribing!',
        },
        notificationClickHandlerAction: 'navigate',
        notificationClickHandlerMatch: 'origin',
      })
      import.meta.env.DEV && OneSignal.Debug.setLogLevel('trace')
      if (!OneSignal.Notifications.permission)
        await OneSignal.Notifications.requestPermission()
      if (OneSignal.User.externalId !== userProfile.data.admin.id) {
        OneSignal.User.externalId && (await OneSignal.logout())
        await OneSignal.login(userProfile.data.admin.id)
      }
      initializeOnesignal.current = true
    }

    initOneSignal()

    return () => {
      if (
        OneSignal.User.onesignalId &&
        (initializeOnesignal.current || !userProfile)
      ) {
        OneSignal.logout()
        initializeOnesignal.current = false
      }
    }
  }, [userProfile])

  return (
    <nav className="bg-white shadow-md w-full">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4 flex flex-wrap justify-between items-center">
        <div className="flex items-center">
          <Link to="/home" className="h-10 w-auto">
            <img src={KohbusLogo} alt="Logo" />
          </Link>
        </div>

        <button className="md:hidden text-neutral-800" onClick={toggleMenu}>
          {isMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>

        <Collapse
          in={!isMobileViewport || isMenuOpen}
          className="w-full md:w-fit flex flex-col justify-center md:flex-row items-center gap-2 pt-4 md:pt-0"
        >
          <Button
            variant="transparent"
            radius="xl"
            color="ink-dark.0"
            size="xs"
            bg="transparent"
          >
            <Link to="/home">
              <Text size="16px" fw="500" lh="1.5">
                Home
              </Text>
            </Link>
          </Button>

          {!userProfile ? (
            <Link to="/manage-booking">
              <Button
                variant="transparent"
                radius="xl"
                color="ink-dark.0"
                size="xs"
                bg="transparent"
              >
                <Text size="16px" fw="500" lh="1.5">
                  <i className="fas fa-ticket mr-2 m-0 p-0 rotate-135" />
                  Manage Booking
                </Text>
              </Button>
            </Link>
          ) : null}

          <Button
            variant="transparent"
            radius="xl"
            color="ink-dark.0"
            size="xs"
            bg="transparent"
          >
            <Link to="/help-support">
              <Text size="16px" fw="500" lh="1.5">
                <i className="fas fa-circle-question mr-2 m-0 p-0" />
                Help & Support
              </Text>
            </Link>
          </Button>
          {userProfile?.data ? (
            <>
              <Link to="/my-booking/list" className="flex items-center gap-2">
                <Button
                  variant="transparent"
                  radius="xl"
                  color="ink-dark.0"
                  size="xs"
                  bg="transparent"
                >
                  <Text size="16px" fw="500" lh="1.5">
                    <i className="fas fa-calendar-star mr-2 m-0 p-0" />
                    My Bookings
                  </Text>
                  {myBookingCount ? (
                    <Badge
                      circle
                      size="xs"
                      color="#D21C1C"
                      className="absolute top-0 left-5 z-10"
                    >
                      {myBookingCount}
                    </Badge>
                  ) : null}
                </Button>
              </Link>
              <Link to="/inbox">
                <Button
                  variant="transparent"
                  radius="xl"
                  color="ink-dark.0"
                  size="xs"
                  bg="transparent"
                >
                  <Text size="16px" fw="500" lh="1.5">
                    <i className="fas fa-envelope mr-2 m-0 p-0" />
                  </Text>
                  {isNotificationTabListSuccess &&
                  notificationTabList.data?.all ? (
                    <Badge
                      circle
                      size="sm"
                      color="#D21C1C"
                      className="absolute top-0 right-4 z-10"
                    >
                      {notificationTabList.data.all}
                    </Badge>
                  ) : null}
                </Button>
              </Link>
            </>
          ) : null}

          {userProfile?.data ? (
            <Menu>
              <Menu.Target>
                <Button
                  size="xl"
                  radius="xl"
                  rightSection={
                    <Avatar
                      w={40}
                      h={40}
                      color="#13A89E"
                      radius="xl"
                      src={userProfile.data.admin.avatar}
                      alt={userProfile.data.admin.name}
                    >
                      {userProfile.data.admin.first_name[0].toUpperCase() +
                        userProfile.data.admin.last_name[0].toUpperCase()}
                    </Avatar>
                  }
                  variant="default"
                  className="max-w-60"
                >
                  <Text component="span" size="16px" fw={400} truncate="end">
                    {userProfile.data.admin.name}
                  </Text>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item className="cursor-default!">
                  <div className="flex items-center gap-3">
                    <Avatar
                      w={56}
                      h={56}
                      color="#13A89E"
                      radius="xl"
                      src={userProfile.data.admin.avatar}
                      alt={userProfile.data.admin.name}
                      flex="none"
                    >
                      {userProfile.data.admin.first_name[0].toUpperCase() +
                        userProfile.data.admin.last_name[0].toUpperCase()}
                    </Avatar>
                    <div className="flex flex-col gap-2 max-w-56">
                      <Text size="16px" fw={400} truncate="end">
                        {userProfile.data.admin.name}
                      </Text>
                      <Text size="16px" fw={400} truncate="end">
                        {userProfile.data.admin.email}
                      </Text>
                    </div>
                  </div>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to="/account"
                  leftSection={<Settings size={20} />}
                >
                  <Text component="span" size="16px" fw={400}>
                    Settings
                  </Text>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to="/auth/logout"
                  leftSection={<LogOut size={20} className="text-[#D21C1C]" />}
                >
                  <Text
                    component="span"
                    size="16px"
                    fw={400}
                    className="text-[#D21C1C]!"
                  >
                    Logout
                  </Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : (
            <div className="flex w-full md:w-fit md:flex-row flex-col items-center gap-3">
              <Button
                component={Link}
                to="/auth/login"
                variant="outline"
                radius="xl"
                bg="white"
                color="primary.6"
                h="44px"
                px="20px"
                py="10px"
                className="w-full! md:w-fit!"
              >
                <Text size="16px" fw="500" lh="1.5" c="primary.6">
                  Login
                </Text>
              </Button>

              <Button
                component={Link}
                to="/auth/register"
                variant="filled"
                radius="xl"
                bg="primary.6"
                h="44px"
                px="20px"
                py="10px"
                className="w-full! md:w-fit!"
              >
                <Text size="16px" fw="500" lh="1.5" c="white">
                  Sign Up
                </Text>
              </Button>
            </div>
          )}
        </Collapse>
      </div>
    </nav>
  )
}

export default memo(Header)
